import { useRef, useState } from 'react';
import type { JSX } from 'react';

import type { FachgruppeDetails, FachgruppeList, StoredFachgruppe } from '@asas/domain';

import { anmeldeUrlMitRueckkehr, erzeugeEntwurfsspeicher } from '../daten/entwurf';
import { klassifiziereFehler } from '../daten/fehler';
import type { FehlerBefund } from '../daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from '../daten/serialisierung';
import { trpc } from '../trpc';

import {
  mitGespeichert,
  namensMeldungen,
  offenMitGespeichert,
  pruefeNamensEntwurf,
  sperrtWeitereVersuche,
} from './fachgruppen';
import {
  FachgruppeMitglieder,
  FachgruppenKopfzeile,
  FachgruppenListe,
  NamensFormular,
} from './FachgruppenAnsicht';

export interface FachgruppenBereichProps {
  /** Die Kontokennung der angemeldeten Person — Inhaber eines Entwurfs (AC-016, §20.2 Punkt 7). */
  readonly inhaber: string;
  /**
   * Bietet die Ansicht Anlegen und Umbenennen an (`darfFachgruppenPflegen`)?
   * Ohne das Recht gar nicht — sonst schriebe der erste Klick eine Audit-Zeile.
   */
  readonly darfPflegen: boolean;
}

type Formular = { readonly modus: 'neu' } | { readonly modus: 'umbenennen'; readonly id: string };

/** Wohin nach der Neuanmeldung? Mit oder ohne gesicherten Namen. */
type Anmeldung = 'keine' | 'mitEntwurf' | 'ohneEntwurf';

/**
 * Die Fachgruppen (AC-104): oeffnen, wer ihnen am Stichtag angehoert (AK-1),
 * anlegen, umbenennen.
 *
 * Nach dem Rezept aus AC-016 wie `MitarbeiterBereich`: Schreibzugriffe ueber
 * die GETEILTE Warteschlange je Fachgruppe, jeder Fehler durch
 * `klassifiziereFehler`, nach Erfolg gilt die SERVERANTWORT.
 *
 * Anlegen und Umbenennen sind wiederholungssicher (eine Wiederholung mit
 * demselben Namen legt nichts doppelt an und ist kein Konflikt), deshalb
 * sperrt ein ungewisser Ausgang das Speichern nicht — anders als beim Anlegen
 * eines Mitarbeiters. Gesperrt wird nur nach einer Ablehnung (403) — und zwar
 * bis zum Neuladen der Seite: Die Fachgruppenleitung darf lesen, aber nicht
 * pflegen; hoebe ein erneutes Oeffnen des Formulars die Sperre auf, schriebe
 * jeder weitere Versuch eine unloeschbare Audit-Zeile (§19).
 *
 * GELADEN WIRD ERST AUF ANFORDERUNG, und angezeigt wird der Bereich nur mit
 * `fachgruppe:read` (`ansichtSichtbar`, AC-000). Ohne das Recht (etwa die
 * Projektleitung, ADR-11) antwortete der Server mit 403, und jede Ablehnung
 * schreibt eine unloeschbare Audit-Zeile (§19). Deshalb gibt es in dieser
 * Datei keinen Effekt (`FachgruppenAnsicht.spec.tsx` haelt das fest).
 */
export function FachgruppenBereich({ inhaber, darfPflegen }: FachgruppenBereichProps): JSX.Element {
  const [liste, setListe] = useState<FachgruppeList | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [ladefehler, setLadefehler] = useState<FehlerBefund | null>(null);
  const [offen, setOffen] = useState<FachgruppeDetails | null>(null);
  const [stichtag, setStichtag] = useState('');
  const [detailLaedt, setDetailLaedt] = useState(false);
  const [detailFehler, setDetailFehler] = useState<FehlerBefund | null>(null);
  const [formular, setFormular] = useState<Formular | null>(null);
  const [name, setName] = useState('');
  const [namensFehler, setNamensFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [anmeldung, setAnmeldung] = useState<Anmeldung>('keine');
  const [zugriffVerweigert, setZugriffVerweigert] = useState(false);
  const [sendet, setSendet] = useState(false);
  // Welche Fachgruppe gerade laedt. Kommt eine Antwort fuer eine andere an
  // (inzwischen eine andere geoeffnet), wird sie verworfen.
  const laedtFuer = useRef<string | null>(null);

  const entwuerfe = erzeugeEntwurfsspeicher({ inhaber });
  // `window` fehlt nur beim Rendern ausserhalb des Browsers (Tests).
  const anmeldeUrl = anmeldeUrlMitRueckkehr(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );
  const lesenGesperrt = sperrtWeitereVersuche(ladefehler) || sperrtWeitereVersuche(detailFehler);

  function entwurfsSchluessel(ziel: Formular): string {
    return entitaetsSchluessel('fachgruppe-formular', ziel.modus === 'neu' ? 'neu' : ziel.id);
  }

  function zuruecksetzen(): void {
    setNamensFehler(null);
    setMeldung(null);
    setAnmeldung('keine');
  }

  async function laden(): Promise<void> {
    setLaedt(true);
    setLadefehler(null);

    try {
      const neu = await trpc.fachgruppen.list.query();

      setListe(neu);

      if (formular?.modus === 'umbenennen' && !neu.fachgruppen.some((f) => f.id === formular.id)) {
        setFormular(null);
      }

      // Die zuletzt angeklickte Fachgruppe, nicht die beim Klick auf „Neu laden"
      // offene: dazwischen kann eine andere geoeffnet worden sein. Nach einer
      // Ablehnung nicht: jeder Versuch schriebe eine Audit-Zeile.
      const zuletzt = offen === null ? null : (laedtFuer.current ?? offen.id);

      if (zuletzt !== null && !sperrtWeitereVersuche(detailFehler)) void oeffnen(zuletzt);
    } catch (error) {
      setLadefehler(klassifiziereFehler(error, { schreibzugriff: false }));
    } finally {
      setLaedt(false);
    }
  }

  /** Oeffnet eine Fachgruppe am eingegebenen Stichtag; ohne Angabe gilt heute laut Server. */
  async function oeffnen(id: string): Promise<void> {
    laedtFuer.current = id;
    setDetailLaedt(true);
    setDetailFehler(null);

    try {
      const details = await trpc.fachgruppen.get.query(
        stichtag === '' ? { id } : { id, on: stichtag },
      );

      if (laedtFuer.current !== details.id) return;

      setOffen(details);
      setStichtag(details.on);
    } catch (error) {
      if (laedtFuer.current !== id) return;

      const befund = klassifiziereFehler(error, { schreibzugriff: false });

      if (befund.art !== 'abgebrochen') setDetailFehler(befund);
    } finally {
      if (laedtFuer.current === id) setDetailLaedt(false);
    }
  }

  /** Oeffnet das Namensformular; ein Entwurf von vor der Neuanmeldung wird angeboten, nie abgeschickt (§20.2 Punkt 9). */
  function formularOeffnen(ziel: Formular, ausgangsname: string): void {
    zuruecksetzen();
    setFormular(ziel);

    const entwurf = entwuerfe.holen(entwurfsSchluessel(ziel), pruefeNamensEntwurf);

    if (entwurf === null) {
      setName(ausgangsname);
    } else {
      setName(entwurf.eingabe);
      setMeldung(
        'Ihre Eingabe von vor der Anmeldung wurde wiederhergestellt. Bitte prüfen und speichern.',
      );
    }
  }

  function fehlerZeigen(error: unknown, ziel: Formular): void {
    const befund = klassifiziereFehler(error, { schreibzugriff: true });

    if (befund.art === 'abgebrochen' && !befund.moeglicherweiseAusgefuehrt) return;

    if (befund.neuAnmelden) {
      setAnmeldung(entwuerfe.merken(entwurfsSchluessel(ziel), name) ? 'mitEntwurf' : 'ohneEntwurf');
    }

    if (befund.art === 'zugriff') setZugriffVerweigert(true);

    const { name: amFeld, allgemein } = namensMeldungen(befund.felder);

    setNamensFehler(amFeld);
    setMeldung(
      befund.moeglicherweiseAusgefuehrt
        ? `${befund.meldung} Der Vorgang könnte bereits gespeichert sein. Ein erneutes Speichern mit demselben Namen legt nichts doppelt an; „Neu laden" zeigt den Stand.`
        : [befund.meldung, ...allgemein].join(' '),
    );
  }

  async function schreiben(ziel: Formular, aufruf: () => Promise<StoredFachgruppe>): Promise<void> {
    zuruecksetzen();
    setSendet(true);

    try {
      const gespeichert = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('fachgruppen', ziel.modus === 'neu' ? 'neu' : ziel.id),
        aufruf,
      );

      setListe((vorher) => (vorher === null ? vorher : mitGespeichert(vorher, gespeichert)));
      setOffen((vorher) => (vorher === null ? vorher : offenMitGespeichert(vorher, gespeichert)));
      entwuerfe.vergessen(entwurfsSchluessel(ziel));
      setFormular(null);
      setMeldung(`Gespeichert: ${gespeichert.name}.`);
    } catch (error) {
      fehlerZeigen(error, ziel);
    } finally {
      setSendet(false);
    }
  }

  function speichern(): void {
    if (formular === null || zugriffVerweigert || sendet || laedt) return;

    if (formular.modus === 'neu') {
      const eingabe = { name };

      void schreiben(formular, () => trpc.fachgruppen.create.mutate(eingabe));
      return;
    }

    const bestehend = liste?.fachgruppen.find((fachgruppe) => fachgruppe.id === formular.id);

    if (bestehend === undefined) return;

    const eingabe = { id: bestehend.id, expectedVersion: bestehend.version, name };

    void schreiben(formular, () => trpc.fachgruppen.rename.mutate(eingabe));
  }

  const kopfzeile = (
    <FachgruppenKopfzeile
      geladen={liste !== null}
      laedt={laedt}
      sendet={sendet}
      ladefehler={ladefehler}
      pflegbar={darfPflegen}
      pflegeGesperrt={zugriffVerweigert}
      anmeldeUrl={anmeldeUrl}
      onLaden={() => {
        void laden();
      }}
      onNeu={() => {
        formularOeffnen({ modus: 'neu' }, '');
      }}
    />
  );

  if (liste === null) {
    return (
      <section>
        <h2>Fachgruppen</h2>
        {kopfzeile}
      </section>
    );
  }

  return (
    <section>
      <h2>Fachgruppen</h2>
      {kopfzeile}

      <FachgruppenListe
        liste={liste}
        offen={offen?.id ?? null}
        sendet={sendet || lesenGesperrt}
        pflegbar={darfPflegen}
        pflegeGesperrt={zugriffVerweigert}
        onOeffnen={(id) => {
          void oeffnen(id);
        }}
        onUmbenennen={(id) => {
          const fachgruppe = liste.fachgruppen.find((kandidat) => kandidat.id === id);

          if (fachgruppe !== undefined)
            formularOeffnen({ modus: 'umbenennen', id }, fachgruppe.name);
        }}
      />

      {meldung === null ? null : <p role="status">{meldung}</p>}
      {anmeldung === 'keine' ? null : (
        <p>
          <a href={anmeldeUrl}>
            {anmeldung === 'mitEntwurf'
              ? 'Neu anmelden – Ihre Eingabe bleibt erhalten'
              : 'Neu anmelden – Ihre Eingabe konnte nicht gesichert werden; bitte vorher notieren'}
          </a>
        </p>
      )}

      {formular === null ? null : (
        <NamensFormular
          modus={formular.modus}
          name={name}
          fehler={namensFehler}
          sendet={sendet}
          gesperrt={laedt || zugriffVerweigert}
          onAendern={setName}
          onSpeichern={speichern}
          onAbbrechen={() => {
            setFormular(null);
            zuruecksetzen();
          }}
        />
      )}

      {detailFehler === null ? null : (
        <p role="alert">
          {[
            'Die Fachgruppe konnte nicht geöffnet werden:',
            detailFehler.meldung,
            ...detailFehler.felder.map((feld) => feld.message),
          ].join(' ')}
        </p>
      )}
      {offen === null ? null : (
        <FachgruppeMitglieder
          details={offen}
          stichtag={stichtag}
          heute={liste.on}
          laedt={detailLaedt}
          gesperrt={lesenGesperrt}
          onStichtag={setStichtag}
          onAnzeigen={() => {
            void oeffnen(offen.id);
          }}
        />
      )}
    </section>
  );
}
